/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { MapBox } from 'components/mapBox'
import { Mdx } from '@ta-interaktiv/contentbricks'
import { groups } from './groups'
// import * as turf from '@turf/turf'
import { labelLayer, mobileCircles, desktopCircles } from './mapLayers'
import { MapKey } from 'components/mapKey'
import { GroupSegment } from 'components/groupSegment'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import standaloneRC from '../../../.taStandalonerc.yml'
groups.sort((a, b) => {
  return b.count - a.count
})
const ARTICLE_ID = standaloneRC.articleId
const ADDITIONAL_SHARES = 1105
const isMobile = browserCheck.isMobile
const restis = require('../_assets/restaurants.geojson')
const hostname = parseHostname().publicationName

function isIE() {
  const ua = navigator.userAgent
  /* MSIE used to detect old browsers and Trident used to newer ones */
  const is_ie = ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1

  return is_ie
}

interface StartView {
  center: [number, number]
  zoom: number
}
const startViewDesktop: StartView = {
  center: [7.4404593913995996, 46.94816842039617],
  zoom: 14.8
}
const startViewMobile: StartView = {
  center: [7.442933437766328, 46.9471295368746],
  zoom: 13
}
const mapBounds: mapboxgl.LngLatBoundsLike = [
  [7.429200527609737, 46.93048617238006],
  [7.456666347920873, 46.963767728099555]
]
const mapBoundsDesktop: mapboxgl.LngLatBoundsLike = [
  [7.3159322702639145, 46.902302174768465],
  [7.549467112948008, 46.98586688849332]
]
const mapBoundsFullscreen: mapboxgl.LngLatBoundsLike = [
  [7.360075830022424, 46.866162256312236],
  [7.51570516004611, 47.0549926184585]
]

export function AppComponent() {
  console.log('Is IE', isIE())
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ThemeSwitch />
      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <h1 className='ui image header'>
            <div className='content'>
              Das sind die grössten Gastrokönige der Stadt Bern
              <div className='sub header'>
                Zürcher Gastro-Riesen beeinflussen auch Berns Gastroszene. Drei
                der neun grössten Beizer in der Bundesstadt kommen aus Zürich.{' '}
              </div>
            </div>
          </h1>
        </div>
      </header>
      <div className='ui vertical segment close'>
        <div className='ui text container'>
          <div className='ta author'>
            <nobr>Claudia Salzmann</nobr> und
            <nobr> Sebastian Broschinski</nobr>
          </div>
          <Mdx file={require('text/intro.mdx')} />
          {!isMobile && <MapKey groups={groups} />}
          {isMobile && <MapKey groups={groups} circles />}
        </div>
      </div>{' '}
      {!isIE() && (
        <div className='ui vertical very fitted segment mapSegment'>
          <MapBox
            maxBounds={isMobile ? mapBounds : mapBoundsDesktop}
            maxBoundsFullscreen={isMobile ? mapBoundsFullscreen : undefined}
            center={isMobile ? startViewMobile.center : startViewDesktop.center}
            zoom={isMobile ? startViewMobile.zoom : startViewDesktop.zoom}
            mapStyle={require('../_assets/basic.json')}
            getMap={(map: mapboxgl.Map) => {
              map.on('move', () => {
                const bounds = map.getBounds()
                const center = map.getCenter()
                const zoom = map.getZoom()
                console.log(
                  bounds._sw + ' ' + bounds._ne + ' | ',
                  center.lng + ', ' + center.lat + ' | ' + zoom
                )
              })
              map.on('style.load', function() {
                // Add restaurant data as source to style json
                map.addSource('restaurants_all', {
                  type: 'geojson',
                  data: restis
                })

                // Preload marker Images
                groups.forEach(g => {
                  map.loadImage(
                    require('images/map_icons_' + g.id + '.png'),
                    function(error: Error, image: ImageData) {
                      if (error) throw error
                      if (!map.hasImage(g.id)) map.addImage(g.id, image)
                    }
                  )
                })

                // Add Layers to Map
                map
                  .addLayer(isMobile ? mobileCircles : desktopCircles) // desktopCircles
                  .addLayer(labelLayer)

                // Change cursor to pointer on hover on a symbol
                map.on('moveend', function() {
                  console.log(map.getZoom())
                })
                map.on('mousemove', function(e) {
                  const features = map
                    .queryRenderedFeatures(e.point)
                    .filter(f => {
                      if (f.layer.id === 'symbols') return f
                    })
                  map.getCanvas().style.cursor = features.length
                    ? 'pointer'
                    : ''
                })
                // Show popup on click
                map.on('click', 'symbols', e => {
                  if (e && e.features && e.features[0].properties) {
                    const props = e.features[0].properties

                    map.addPopup(
                      <PopUp
                        id={props.id}
                        color={props.color}
                        group={props.group}
                        rest={props.rest}
                        adr={props.adr}
                        rating={props.rating}
                      />,
                      e.lngLat
                    )
                  }
                })
              })
            }}
            onActivation={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: 'map activated'
              })
            }}
            onDeactivation={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: 'map deactivated'
              })
            }}
            FullscreenMapKey={
              isMobile ? <MapKey groups={groups} circles noCount /> : undefined
            }
          />
        </div>
      )}
      {isIE() && (
        <div className='ui vertical segment'>
          <img src={require('images/iemap.png')} />
        </div>
      )}
      <div className='ui vertical   segment '>
        <div className='ui text container'>
          <Mdx file={require('text/part_1.mdx')} />
        </div>
      </div>
      {JSON.parse(JSON.stringify(groups))
        .sort((a, b) => {
          return a.sort - b.sort
        })
        .map((group, i) => {
          return (
            <React.Fragment key={i}>
              <GroupSegment group={group} />
            </React.Fragment>
          )
        })}
      {/* <div className='ui text container'>
        <Mdx file={require('text/others.mdx')} />
      </div> */}
      <div className='ui text container box'>
        <Mdx file={require('text/box.mdx')} />
        <br />
        <a href='https://interaktiv.tagesanzeiger.ch/2019/was-zuerich-wissen-will/?nosome'>
          <img
            src='https://interaktiv.tagesanzeiger.ch/2019/was-zuerich-wissen-will/mini-teaser-collecting.png'
            width='100%'
          />
        </a>
      </div>
      <Footer
        additionalShares={ADDITIONAL_SHARES}
        articleId={ARTICLE_ID}
        credits={[
          ['Text und Recherche', 'Claudia Salzmann'],
          ['Umsetzung', 'Sebastian Broschinski']
        ]}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
      />
    </div>
  )
}

const PopUp = ({
  id,
  color,
  group,
  rest,
  adr,
  rating
}: {
  id: string
  color: string
  group: string
  rest: string
  adr: string
  rating: string
}) => {
  return (
    <div className='popUp'>
      <img src={require('images/map_icons_' + id + '.png')} />
      <div className='group' style={{ color: color }}>
        {group}
      </div>
      <div className='name'>{rest}</div>
      <div className='adress'>{adr}</div>
      <div className='adress'>{rating}</div>
    </div>
  )
}
